import React from 'react';
import { FaSlidersH } from 'react-icons/fa';
import Loader from '../../shared/components/layout/Loader';
import SearchInput from '../../shared/components/table/SearchInput';
import SearchableSelect from '../../shared/components/table/SearchableSelect';
import { INTEGRATION_CLIENT_OPTIONS } from '../../shared/config/constants';

const selectClass =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500';
const switchTrack =
  'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border border-transparent bg-gray-200 transition-colors peer-checked:bg-indigo-600 peer-focus-visible:outline peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500 peer-focus-visible:ring-offset-2 after:pointer-events-none after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:ring-1 after:ring-gray-900/5 after:transition-transform peer-checked:after:translate-x-[1.25rem]';

const ReportsFilters = ({
  globalFilter,
  setGlobalFilter,
  filtersMenuOpen,
  setFiltersMenuOpen,
  filtersPanelRef,
  hasActivePanelFilters,
  activeFilterCount = 0,
  clearPanelFilters,
  tools,
  selectedTool,
  setSelectedTool,
  selectedOrg,
  selectedOrgLabel,
  setSelectedOrg,
  selectedUser,
  selectedUserLabel,
  setSelectedUser,
  loadOrgOptions,
  loadUserOptions,
  filterFailed,
  setFilterFailed,
  filterIntegrations,
  setFilterIntegrations,
  selectedIntegrationClient,
  setSelectedIntegrationClient,
  filterStartDate,
  setFilterStartDate,
  filterEndDate,
  setFilterEndDate,
  handleTimestampsExport,
  timestampsExportLoading,
  handleReportsExport,
  exportLoading,
}) => {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <SearchInput
        value={globalFilter}
        onChange={setGlobalFilter}
        placeholder="Search reports..."
      />
      <div className="ml-auto flex shrink-0 items-center gap-2">
        <div className="relative" ref={filtersPanelRef}>
          <button
            type="button"
            aria-expanded={filtersMenuOpen}
            aria-haspopup="true"
            aria-label={
              activeFilterCount > 0 ? `Filters (${activeFilterCount} active)` : 'Filters'
            }
            onClick={() => setFiltersMenuOpen((o) => !o)}
            className={`relative inline-flex h-[42px] w-[42px] items-center justify-center rounded-md border shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              hasActivePanelFilters || filtersMenuOpen
                ? 'border-indigo-300 bg-indigo-50 text-indigo-800'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FaSlidersH className="text-lg" aria-hidden />
            {activeFilterCount > 0 && (
              <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-indigo-600 px-1 text-[11px] font-semibold leading-none text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          {filtersMenuOpen && (
            <div
              className="absolute right-[52px] -top-[2px] z-50  w-[min(100vw-2rem,20rem)] rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
              role="dialog"
              aria-label="Report filters"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">Failed</span>
                    <label className="inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        role="switch"
                        className="peer sr-only"
                        checked={!!filterFailed}
                        onChange={(e) => setFilterFailed(e.target.checked ? true : null)}
                      />
                      <span className={switchTrack} aria-hidden />
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">Integrations</span>
                    <label className="inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        role="switch"
                        className="peer sr-only"
                        checked={filterIntegrations}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setFilterIntegrations(on);
                          if (!on) setSelectedIntegrationClient('');
                        }}
                      />
                      <span className={switchTrack} aria-hidden />
                    </label>
                  </div>
                </div>
                <div>
                  <label htmlFor="filter-tool" className="mb-1.5 block text-xs font-medium text-gray-600">
                    Tool
                  </label>
                  <select
                    id="filter-tool"
                    value={selectedTool || ''}
                    onChange={(e) => setSelectedTool(e.target.value)}
                    className={`${selectClass} ${!selectedTool && 'text-gray-500'}`}
                  >
                    <option value="">All tools</option>
                    {tools.map((tool) => (
                      <option key={tool.id} value={tool.id}>
                        {tool.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="mb-1.5 text-xs font-medium text-gray-600">Organisation</div>
                  <SearchableSelect
                    id="filter-org"
                    value={selectedOrg || ''}
                    selectedLabel={selectedOrgLabel}
                    onChange={setSelectedOrg}
                    loadOptions={loadOrgOptions}
                    placeholder="All organisations"
                    className="w-full"
                  />
                </div>
                <div>
                  <div className="mb-1.5 text-xs font-medium text-gray-600">User</div>
                  <SearchableSelect
                    id="filter-user"
                    value={selectedUser || ''}
                    selectedLabel={selectedUserLabel}
                    onChange={setSelectedUser}
                    loadOptions={loadUserOptions}
                    placeholder="All users"
                    className="w-full"
                  />
                </div>
                {filterIntegrations && (
                  <div>
                    <label htmlFor="filter-integration" className="mb-1.5 block text-xs font-medium text-gray-600">
                      Integration
                    </label>
                    <select
                      id="filter-integration"
                      value={selectedIntegrationClient}
                      onChange={(e) => setSelectedIntegrationClient(e.target.value)}
                      className={`${selectClass} ${!selectedIntegrationClient && 'text-gray-500'}`}
                    >
                      <option value="">All integrations</option>
                      {INTEGRATION_CLIENT_OPTIONS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="filter-start" className="mb-1.5 block text-xs font-medium text-gray-600">
                      Start Date
                    </label>
                    <input
                      id="filter-start"
                      type="date"
                      value={filterStartDate}
                      onChange={(e) => setFilterStartDate(e.target.value)}
                      className={selectClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="filter-end" className="mb-1.5 block text-xs font-medium text-gray-600">
                      End Date
                    </label>
                    <input
                      id="filter-end"
                      type="date"
                      value={filterEndDate}
                      onChange={(e) => setFilterEndDate(e.target.value)}
                      className={selectClass}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {selectedOrg && (
                    <button
                      type="button"
                      onClick={handleTimestampsExport}
                      disabled={timestampsExportLoading}
                      className="w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-80"
                    >
                      {timestampsExportLoading ? 'Exporting…' : 'Export timestamps'}
                    </button>
                  )}
                  {hasActivePanelFilters && (
                    <button
                      type="button"
                      onClick={() => {
                        clearPanelFilters();
                        setFiltersMenuOpen(false);
                      }}
                      className="w-full rounded-md border border-indigo-700 bg-indigo-700 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleReportsExport}
          disabled={exportLoading}
          className="flex min-w-[5.5rem] shrink-0 items-center justify-center rounded-md border border-indigo-700 bg-indigo-700 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-80"
        >
          {exportLoading ? (
            <Loader variant="small" className="flex items-center justify-center" />
          ) : (
            'Export'
          )}
        </button>
      </div>
    </div>
  );
};

export default ReportsFilters;
